import { isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { Pagination, Payment, PaymentFormValues, PaymentListItem } from "../types"
import { toDateTimeOrNull, toStringOrNull } from "src/features/admin/screen-shared"

const ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/payments`

export const usePayments = (page: number) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading } = useSWR(
    `${ENDPOINT}?page=${page}`,
    async (url: string) =>
      (await getWithToken(url)).data as { payments: PaymentListItem[]; pagination: Pagination },
    { revalidateOnFocus: false }
  )

  return {
    payments: data?.payments ?? [],
    pagination: data?.pagination ?? null,
    error: error ?? null,
    isLoading,
  }
}

export const usePayment = (id: number | null) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading, mutate } = useSWR(
    id ? `${ENDPOINT}/${id}` : null,
    async (url: string) => (await getWithToken(url)).data as { payment: Payment },
    { revalidateOnFocus: false }
  )

  return { payment: data?.payment ?? null, error: error ?? null, isLoading, mutate }
}

type SaveResult = { ok: true } | { ok: false; message: string }

const toMessage = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string"
    ? e.response.data.message
    : fallback

const toRequestBody = (values: PaymentFormValues) => ({
  userId: toStringOrNull(values.userId),
  paymentableType: values.paymentableType,
  paymentableId: toStringOrNull(values.paymentableId),
  amount: toStringOrNull(values.amount),
  paymentAt: toDateTimeOrNull(values.paymentAt),
})

/** 入金の更新・削除。失敗したときは API のメッセージを返す */
export const useSavePayment = () => {
  const { patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)

  const run = useCallback(
    async (request: () => Promise<unknown>, fallback: string): Promise<SaveResult> => {
      setIsSaving(true)
      try {
        await request()
        return { ok: true }
      } catch (e) {
        return { ok: false, message: toMessage(e, fallback) }
      } finally {
        setIsSaving(false)
      }
    },
    []
  )

  const update = useCallback(
    (id: number, values: PaymentFormValues) =>
      run(() => patchWithToken(`${ENDPOINT}/${id}`, toRequestBody(values)), "保存に失敗しました"),
    [patchWithToken, run]
  )

  const destroy = useCallback(
    (id: number) => run(() => deleteWithToken(`${ENDPOINT}/${id}`), "削除に失敗しました"),
    [deleteWithToken, run]
  )

  return { update, destroy, isSaving }
}
